/**
 * Phase 3 Feature K — default calculator catalog (Firestore seed mirror; keep in sync with functions/src/ddxSeed/ddxCalculatorsSeedData.js).
 * Diagnosis ids align with Phase 1 KB (`phase1KbSeedData.js`). Feature ids align where possible.
 */

import { TEMPORAL_BUCKETS, VERIFICATION_STATUS } from './ddxTemporalTaxonomy.js';

const TB = TEMPORAL_BUCKETS;

/** @type {object[]} */
export const DDX_CALCULATOR_CATALOG_SEED = [
  {
    id: 'calc_wells_pe',
    label: 'Wells Criteria for PE',
    calculatorType: 'risk_stratification',
    externalRef: 'mdcalc_115',
    inputs: [
      { paramId: 'clinical_signs_dvt', label: 'Clinical signs/symptoms of DVT', type: 'boolean', mapToFeatures: ['feat_leg_swelling_unilateral'], points: 3 },
      { paramId: 'heart_rate_gt_100', label: 'Heart rate > 100 bpm', type: 'boolean', mapToFeatures: ['feat_tachycardia'], points: 1.5 },
    ],
    triggerConditions: {
      diagnosisIds: ['dx_pe'],
      minimumDxScore: 0.3,
      temporalBuckets: [TB.HYPERACUTE, TB.ACUTE, TB.SUBACUTE],
    },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_perc',
    label: 'PERC Rule',
    calculatorType: 'risk_stratification',
    externalRef: 'mdcalc_356',
    inputs: [
      { paramId: 'hr_ge_100', label: 'HR ≥ 100', type: 'boolean', mapToFeatures: ['feat_tachycardia'], points: 1 },
      { paramId: 'leg_swelling', label: 'Unilateral leg swelling', type: 'boolean', mapToFeatures: ['feat_leg_swelling_unilateral'], points: 1 },
    ],
    triggerConditions: {
      diagnosisIds: ['dx_pe'],
      minimumDxScore: 0.25,
      temporalBuckets: [TB.ACUTE, TB.SUBACUTE, TB.CHRONIC, TB.INDETERMINATE],
    },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_heart_score',
    label: 'HEART Score',
    calculatorType: 'risk_stratification',
    externalRef: 'mdcalc_2198',
    inputs: [
      { paramId: 'st_deviation', label: 'ST deviation on ECG', type: 'boolean', mapToFeatures: [], points: 2 },
      { paramId: 'typical_angina', label: 'Typical angina features', type: 'boolean', mapToFeatures: [], points: 2 },
    ],
    triggerConditions: {
      diagnosisIds: ['dx_stemi', 'dx_nstemi'],
      minimumDxScore: 0.25,
      temporalBuckets: [TB.HYPERACUTE, TB.ACUTE, TB.SUBACUTE],
    },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_ottawa_ankle',
    label: 'Ottawa Ankle Rules',
    calculatorType: 'disposition',
    externalRef: 'mdcalc_59',
    inputs: [{ paramId: 'ankle_pain', label: 'Malleolar zone pain', type: 'boolean', mapToFeatures: [], points: 1 }],
    // KB has no dedicated MSK fracture dx; leg CC often surfaces DVT — pragmatic trigger for ankle injury pathways.
    triggerConditions: { diagnosisIds: ['dx_dvt'], minimumDxScore: 0.2, temporalBuckets: [TB.ACUTE, TB.SUBACUTE] },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_ottawa_knee',
    label: 'Ottawa Knee Rules',
    calculatorType: 'disposition',
    externalRef: 'mdcalc_58',
    inputs: [{ paramId: 'knee_injury', label: 'Acute knee injury', type: 'boolean', mapToFeatures: [], points: 1 }],
    triggerConditions: { diagnosisIds: ['dx_dvt'], minimumDxScore: 0.15, temporalBuckets: [TB.ACUTE] },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_canadian_cspine',
    label: 'Canadian C-Spine Rule',
    calculatorType: 'disposition',
    externalRef: 'mdcalc_99',
    inputs: [{ paramId: 'neck_pain', label: 'Dangerous mechanism / neck pain', type: 'boolean', mapToFeatures: [], points: 1 }],
    triggerConditions: { diagnosisIds: ['dx_sah'], minimumDxScore: 0.2, temporalBuckets: [TB.ACUTE, TB.SUBACUTE] },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_curb65',
    label: 'CURB-65',
    calculatorType: 'severity',
    externalRef: 'mdcalc_553',
    inputs: [
      { paramId: 'confusion', label: 'Confusion', type: 'boolean', mapToFeatures: [], points: 1 },
      { paramId: 'resp_rate_ge_30', label: 'RR ≥ 30', type: 'boolean', mapToFeatures: [], points: 1 },
    ],
    triggerConditions: { diagnosisIds: ['dx_pneumonia'], minimumDxScore: 0.25, temporalBuckets: [TB.ACUTE, TB.SUBACUTE] },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_chads2_vasc',
    label: 'CHA₂DS₂-VASc',
    calculatorType: 'risk_stratification',
    externalRef: 'mdcalc_339',
    inputs: [
      { paramId: 'htn', label: 'Hypertension', type: 'boolean', mapToFeatures: [], points: 1 },
      { paramId: 'diabetes', label: 'Diabetes', type: 'boolean', mapToFeatures: [], points: 1 },
    ],
    triggerConditions: {
      diagnosisIds: ['dx_svt'],
      minimumDxScore: 0.2,
      temporalBuckets: [TB.ACUTE, TB.SUBACUTE, TB.CHRONIC],
    },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_wells_dvt',
    label: 'Wells Criteria for DVT',
    calculatorType: 'risk_stratification',
    externalRef: 'mdcalc_47',
    inputs: [
      { paramId: 'paralysis', label: 'Paralysis / paresis / immobilization', type: 'boolean', mapToFeatures: [], points: 1 },
      { paramId: 'localized_tenderness', label: 'Localized tenderness', type: 'boolean', mapToFeatures: [], points: 1 },
      { paramId: 'entire_leg_swollen', label: 'Entire leg swollen', type: 'boolean', mapToFeatures: ['feat_leg_swelling_unilateral'], points: 1 },
    ],
    triggerConditions: { diagnosisIds: ['dx_dvt'], minimumDxScore: 0.3, temporalBuckets: [TB.ACUTE, TB.SUBACUTE] },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
  {
    id: 'calc_centor',
    label: 'Centor Score',
    calculatorType: 'diagnosis',
    externalRef: 'mdcalc_712',
    inputs: [
      { paramId: 'exudate', label: 'Tonsillar exudate', type: 'boolean', mapToFeatures: [], points: 1 },
      { paramId: 'nodes', label: 'Tender anterior cervical nodes', type: 'boolean', mapToFeatures: [], points: 1 },
      { paramId: 'fever', label: 'Fever / history of fever', type: 'boolean', mapToFeatures: [], points: 1 },
    ],
    triggerConditions: { diagnosisIds: ['dx_meningitis'], minimumDxScore: 0.15, temporalBuckets: [TB.ACUTE, TB.SUBACUTE] },
    trustTier: 1,
    verificationStatus: VERIFICATION_STATUS.VERIFIED,
  },
];
